import { Component, DestroyRef, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { ScheduledReportsSftpControlComponent } from 'dashboard/components/content-dashboard/scheduled-reports-dashboard-widget/controls/scheduled-reports-sftp-control/scheduled-reports-sftp-control.component';
import { ScheduledReportsEmailSettingsModalComponent } from 'dashboard/components/content-dashboard/scheduled-reports-dashboard-widget/modals/scheduled-reports-email-settings-modal/scheduled-reports-email-settings-modal.component';
import { scheduledReportFrequencyTranslationMap } from 'dashboard/constants/scheduled-report-frequency-translation-map.constant';
import { scheduledReportTypeOptions } from 'dashboard/constants/scheduled-report-type-options';
import { ScheduledReportFrequency } from 'dashboard/enums/scheduled-report-frequency.enum';
import { ScheduledReportType } from 'dashboard/enums/scheduled-report-type.enum';
import { ScheduledReportSftpSetting } from 'dashboard/interfaces/scheduled-report-sftp-setting.interface';
import { DashboardService } from 'dashboard/services/dashboard.service';
import { updateTreeValidity } from 'dynamic-form/util/update-tree-validity';
import { NumberValidator } from 'dynamic-form/validators/number-validator';
import { BaseModal } from 'shared/abstract/base-modal';
import { FormErrorComponent } from 'shared/components/form-error/form-error.component';
import { MultiInputControlComponent } from 'shared/components/multi-input-control/multi-input-control.component';
import { TypeaheadSelectComponent } from 'shared/components/typeahead-select/typeahead-select.component';
import { IfNotDisabledDirective } from 'shared/directives/if-not-disabled.directive';
import { ModalService, ToastService } from 'shared/services';
import { SharedModule } from 'shared/shared.module';
import { DependentRequiredValidator } from 'shared/validators/dependent-required-validator-modifier';
import { formGroupCheckboxesSelectionRequired } from 'shared/validators/form-group-checkboxes-selection-req';
import { ObjectRequiredValidator } from 'shared/validators/object-required.validator';

type FieldStatus = 'disable' | 'enable';

const frequencyFormFieldStatusMap: {
    [key in ScheduledReportFrequency]: {
        days: FieldStatus;
        dayOfMonth: FieldStatus;
        time: FieldStatus;
    };
} = {
    [ScheduledReportFrequency.DAILY]: { days: 'disable', dayOfMonth: 'disable', time: 'enable' },
    [ScheduledReportFrequency.WEEKLY]: { days: 'enable', dayOfMonth: 'disable', time: 'enable' },
    [ScheduledReportFrequency.MONTHLY]: { days: 'disable', dayOfMonth: 'enable', time: 'enable' },
    [ScheduledReportFrequency.MANUAL]: { days: 'disable', dayOfMonth: 'disable', time: 'disable' },
    [ScheduledReportFrequency.UNKNOWN]: { days: 'disable', dayOfMonth: 'disable', time: 'disable' },
};

@Component({
    selector: 'app-scheduled-reports-edit-modal',
    templateUrl: './scheduled-reports-edit-modal.component.html',
    styleUrls: ['./scheduled-reports-edit-modal.component.scss'],
    imports: [
        SharedModule,
        TranslateModule,
        IfNotDisabledDirective,
        MultiInputControlComponent,
        FormErrorComponent,
        TypeaheadSelectComponent,
        ScheduledReportsSftpControlComponent,
    ],
})
export class ScheduledReportsEditModalComponent extends BaseModal implements OnInit {
    @ViewChild(MultiInputControlComponent) multiSelectInput!: MultiInputControlComponent;

    public readonly ScheduledReportFrequency = ScheduledReportFrequency;
    public readonly scheduledReportFrequencyTranslationMap = scheduledReportFrequencyTranslationMap;
    public data: any;
    public emails: { label: string }[] = [];
    public emailSettingsDefaults!: { body: string; subject: string };
    public sftpData: ScheduledReportSftpSetting[] = [];
    public scheduledReportTypes: {
        label: string;
        value: ScheduledReportType;
    }[] = scheduledReportTypeOptions as { label: string; value: ScheduledReportType }[];
    public form: FormGroup<{
        scheduledReportType: FormControl<string>;
        frequency: FormControl<ScheduledReportFrequency>;
        days: FormGroup<{
            sunday: FormControl<boolean>;
            saturday: FormControl<boolean>;
            tuesday: FormControl<boolean>;
            wednesday: FormControl<boolean>;
            thursday: FormControl<boolean>;
            friday: FormControl<boolean>;
            monday: FormControl<boolean>;
        }>;
        dayOfMonth: FormControl<number | null>;
        time: FormControl<string>;
        emails: FormControl<any>;
        emailSettings: FormGroup<{
            subject: FormControl<string>;
            body: FormControl<string>;
        }>;
        selectedSftpSettingsIds: FormControl<string[]>;
    }> = new FormGroup({
        scheduledReportType: new FormControl('', { nonNullable: true, validators: Validators.required }),
        frequency: new FormControl<ScheduledReportFrequency>(ScheduledReportFrequency.MANUAL, {
            nonNullable: true,
            validators: Validators.required,
        }),
        days: new FormGroup(
            {
                monday: new FormControl(false, { nonNullable: true }),
                tuesday: new FormControl(false, { nonNullable: true }),
                wednesday: new FormControl(false, { nonNullable: true }),
                thursday: new FormControl(false, { nonNullable: true }),
                friday: new FormControl(false, { nonNullable: true }),
                saturday: new FormControl(false, { nonNullable: true }),
                sunday: new FormControl(false, { nonNullable: true }),
            },
            [formGroupCheckboxesSelectionRequired],
        ),
        dayOfMonth: new FormControl<number>(
            {
                value: 1,
                disabled: true,
            },
            {
                validators: [Validators.required, NumberValidator({ min: 1, max: 31, step: 1 })],
            },
        ),
        time: new FormControl({ value: '00:00', disabled: true }, { nonNullable: true, validators: Validators.required }),
        emails: new FormControl([], {
            nonNullable: true,
            validators: [
                DependentRequiredValidator('selectedSftpSettingsIds', ObjectRequiredValidator(), ObjectRequiredValidator()),
                this.emailsValidator.bind(this),
            ],
        }),
        emailSettings: new FormGroup({
            subject: new FormControl('', { nonNullable: true, validators: Validators.required }),
            body: new FormControl('', { nonNullable: true, validators: Validators.required }),
        }),
        selectedSftpSettingsIds: new FormControl([] as string[], {
            nonNullable: true,
            validators: [DependentRequiredValidator('emails', ObjectRequiredValidator(), ObjectRequiredValidator())],
        }),
    });

    constructor(
        private modalService: ModalService,
        private dashboardService: DashboardService,
        private toastService: ToastService,
        private destroyRef: DestroyRef,
    ) {
        super();
    }

    public ngOnInit() {
        // There is no way to initialize an already disabled Form Group without using a form builder or this
        this.form.get('days')!.disable();

        if (this.data.id) {
            this.form.get('scheduledReportType')!.disable();
        }

        this.dashboardService
            .getScheduledReportRenderData()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((data) => {
                this.emails = data.emailList.map((email: string) => {
                    return { label: email };
                });
                this.emailSettingsDefaults = data.emailSettingsDefaults;

                if (!this.data.id) {
                    this.form.get('emailSettings')?.patchValue(this.emailSettingsDefaults);
                }

                this.scheduledReportTypes = data.scheduledReportTypes.map((reportType) =>
                    this.getScheduledReportTypeOption(reportType.value),
                );

                this.sftpData = data.sftpData;
            });

        if (this.data.id) {
            this.dashboardService
                .getScheduledReportById(this.data.id)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe((data) => {
                    this.form.patchValue(data);
                    this.onFrequencyChange();
                });
        }
    }

    public onFrequencyChange() {
        const frequency = this.form.get('frequency')!.value as ScheduledReportFrequency;
        const fieldMap = frequencyFormFieldStatusMap[frequency];
        const fieldKeys = Object.keys(fieldMap) as (keyof typeof fieldMap)[];

        // Set each relevant input (time, days, dayOfMonth) to the correct status (enable, disable)
        fieldKeys.forEach((key) => {
            const formControl = this.form.get(key)!;

            if (fieldMap[key] === 'disable') {
                formControl.disable();
                formControl.reset();
                formControl.markAsUntouched();
            } else {
                formControl.enable();
            }
        });
    }

    /**
     * Open the email settings modal and on closing save its contents in memory
     */
    public openEmailSettingsModal() {
        const emailSettingsFrom = this.form.get('emailSettings')!;
        const data = {
            emailSettingsDefaults: this.emailSettingsDefaults,
            form: emailSettingsFrom,
            type: 'center',
        };
        const oldEmailSettingsValues = emailSettingsFrom.value;

        this.modalService
            .openModal(ScheduledReportsEmailSettingsModalComponent, data)
            .onClose.pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((saved: boolean) => {
                if (!saved) {
                    emailSettingsFrom.patchValue(oldEmailSettingsValues);
                }
            });
    }

    public saveScheduledReport() {
        this.multiSelectInput.onEnterKey(new Event('keydown'));
        updateTreeValidity(this.form);
        this.form.markAllAsTouched();

        if (this.form.invalid) {
            return;
        }

        const data = {
            ...this.form.value,
            frequency: parseInt(this.form.get('frequency')!.value as any) as ScheduledReportFrequency,
        };

        this.dashboardService
            .saveScheduledReport(data, this.data.id)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.toastService.showToast('dashboard.scheduledReports.toast.successfullySaved', 'success');
                this.closeModalInstance(true);
            });
    }

    public emailsValidator(control: AbstractControl) {
        if (!control.value || control.value.length === 0) {
            return null;
        }

        let emailArray!: string[];

        if (Array.isArray(control.value)) {
            emailArray = control.value;
        } else if (typeof control.value === 'string') {
            emailArray = control.value.split(',');
        } else {
            return { emailListPattern: true };
        }

        for (let i = 0; i < emailArray.length; i++) {
            const email = emailArray[i];

            // RFC 2822 compliant email regex
            if (
                !/^[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?\.)+[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?$/.test(
                    email,
                )
            ) {
                return { emailListPattern: true };
            }
        }

        return null;
    }

    public closeModal() {
        this.closeModalInstance();
    }

    private getScheduledReportTypeOption(reportTypeValue: ScheduledReportType) {
        const index = scheduledReportTypeOptions.findIndex((option) => option.value === reportTypeValue);
        return scheduledReportTypeOptions[index];
    }
}
